/**
 * Os presets do seletor de frequência (AB#87330): os casos comuns sem pedir cron
 * a ninguém. Tudo é gravado como cron padrão — é o que o gatilho do engine lê —,
 * e todo cron que um preset gera volta para o mesmo preset.
 */
import { normalizeCron, parseCron } from './cron.js';
export const MINUTE_STEPS = [5, 10, 15, 30];
export const HOUR_STEPS = [1, 2, 4, 6, 12];
export const MONTH_DAY_MAX = 28;
/** A ordem do seletor: o cron fica por último. */
export const PRESET_KINDS = [
    'everyMinutes',
    'everyHours',
    'daily',
    'weekdays',
    'weekly',
    'monthly',
    'custom',
];
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
const WEEKDAYS = '1-5';
export function isValidTime(value) {
    return TIME.test(value);
}
export function isValidWindow(window) {
    return (Number.isInteger(window.from) &&
        Number.isInteger(window.to) &&
        window.from >= 0 &&
        window.to <= 24 &&
        window.from < window.to);
}
export function presetToCron(preset) {
    switch (preset.kind) {
        case 'everyMinutes':
            return `*/${preset.minutes} ${windowHours(preset.window, 1)} * * ${preset.weekdaysOnly ? WEEKDAYS : '*'}`;
        case 'everyHours':
            return `0 ${windowHours(preset.window, preset.hours)} * * ${preset.weekdaysOnly ? WEEKDAYS : '*'}`;
        case 'daily':
            return `${timeFields(preset.time)} * * *`;
        case 'weekdays':
            return `${timeFields(preset.time)} * * ${WEEKDAYS}`;
        case 'weekly': {
            const days = [...new Set(preset.days.map((d) => d % 7))].sort((a, b) => a - b);
            return `${timeFields(preset.time)} * * ${days.length > 0 ? days.join(',') : '1'}`;
        }
        case 'monthly':
            return `${timeFields(preset.time)} ${preset.day === 'last' ? 'L' : preset.day} * *`;
        case 'custom':
            return normalizeCron(preset.cron);
    }
}
/** O preset que dispara exatamente como este cron; o que não couber em nenhum volta como `custom`. */
export function cronToPreset(cron) {
    const custom = { kind: 'custom', cron: normalizeCron(cron) };
    const parsed = parseCron(cron);
    if (!parsed || !parsed.month.any)
        return custom;
    const weekdaysOnly = isWorkweek(parsed.weekday.values) && !parsed.weekday.any;
    const anyWeekday = parsed.weekday.any || parsed.weekday.values.length === 7;
    const anyDay = parsed.day.any;
    const intervalDays = anyDay && (anyWeekday || weekdaysOnly);
    const minuteStep = stepOf(parsed.minute.values, 0, 59);
    if (intervalDays && minuteStep !== null && includes(MINUTE_STEPS, minuteStep) && first(parsed.minute.values) === 0) {
        const window = hourWindow(parsed.hour.values, 1);
        if (window !== undefined)
            return { kind: 'everyMinutes', minutes: minuteStep, window, weekdaysOnly };
    }
    const single = singleTime(parsed);
    // Horas em lista (`8,12`) são horários escolhidos, não um intervalo — como na descrição.
    const hourInterval = !single && !parsed.hour.token.includes(',');
    if (intervalDays && hourInterval && parsed.minute.values.length === 1 && first(parsed.minute.values) === 0) {
        const hours = parsed.hour.values.length > 1 ? (parsed.hour.values[1] ?? 0) - first(parsed.hour.values) : null;
        if (hours !== null && includes(HOUR_STEPS, hours)) {
            const window = hourWindow(parsed.hour.values, hours);
            if (window !== undefined)
                return { kind: 'everyHours', hours, window, weekdaysOnly };
        }
    }
    if (!single)
        return custom;
    if (anyDay && anyWeekday)
        return { kind: 'daily', time: single };
    if (anyDay && weekdaysOnly)
        return { kind: 'weekdays', time: single };
    if (anyDay)
        return { kind: 'weekly', days: parsed.weekday.values, time: single };
    if (anyWeekday && parsed.day.lastDay)
        return { kind: 'monthly', day: 'last', time: single };
    if (anyWeekday && parsed.day.values.length === 1 && first(parsed.day.values) <= MONTH_DAY_MAX) {
        return { kind: 'monthly', day: first(parsed.day.values), time: single };
    }
    return custom;
}
function windowHours(window, step) {
    const suffix = step > 1 ? `/${step}` : '';
    if (!window || (window.from === 0 && window.to === 24))
        return step > 1 ? `*${suffix}` : '*';
    const last = window.to - 1;
    if (last === window.from)
        return String(window.from);
    return `${window.from}-${last}${suffix}`;
}
function timeFields(time) {
    const match = TIME.exec(time) ?? TIME.exec('09:00');
    return `${Number(match[2])} ${Number(match[1])}`;
}
function singleTime(parsed) {
    if (parsed.minute.values.length !== 1 || parsed.hour.values.length !== 1)
        return null;
    return `${pad(first(parsed.hour.values))}:${pad(first(parsed.minute.values))}`;
}
/**
 * A janela das horas de um intervalo: `null` quando cobre o dia todo, `undefined`
 * quando as horas não formam uma sequência regular a partir da primeira.
 */
function hourWindow(hours, step) {
    if (hours.length === 0)
        return undefined;
    for (let i = 1; i < hours.length; i++)
        if ((hours[i] ?? 0) - (hours[i - 1] ?? 0) !== step)
            return undefined;
    const from = first(hours);
    const end = last(hours);
    // Sem janela: começa à meia-noite e vai até onde o passo alcança no dia.
    if (from === 0 && end + step > 23)
        return null;
    // O fim volta como o próximo passo: 06–18 a cada 2 h grava `6-17/2` e reabre 06–18.
    return { from, to: Math.min(end + step, 24) };
}
/** O passo de uma sequência que vai de `min` até onde ele alcança dentro de `max`. */
function stepOf(values, min, max) {
    if (values.length < 2 || first(values) !== min)
        return null;
    const step = (values[1] ?? 0) - first(values);
    for (let i = 1; i < values.length; i++)
        if ((values[i] ?? 0) - (values[i - 1] ?? 0) !== step)
            return null;
    return last(values) + step > max ? step : null;
}
function isWorkweek(days) {
    return days.join(',') === '1,2,3,4,5';
}
function includes(list, value) {
    return list.includes(value);
}
function first(values) {
    return values[0] ?? 0;
}
function last(values) {
    return values[values.length - 1] ?? 0;
}
function pad(value) {
    return String(value).padStart(2, '0');
}
//# sourceMappingURL=preset.js.map